/**
 * CDateTimeRangePicker
 * @author Tevin
 */

import React from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import moment from 'moment';
import locale from 'antd/es/date-picker/locale/zh_CN';
import 'moment/locale/zh-cn';
import './cDatePicker.scss';

export class CDateTimeRangePicker extends React.Component {
    static propTypes = {
        // 样式
        style: PropTypes.object,
        // 允许清空
        allowClear: PropTypes.bool,
        // 占位文字
        placeholder: PropTypes.arrayOf(PropTypes.string),
        // 是否显示秒钟
        showSecond: PropTypes.bool,
        // 限制最多可选天数
        limitDays: PropTypes.number,
        // 值
        value: PropTypes.string,
        // 回调
        onChange: PropTypes.func,
    };

    static defaultProps = {
        allowClear: false,
        showSecond: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            // 当前已选的日期，开始日期与结束日期两项，计算禁用天数专用
            pickDates: null,
        };
        this._data = {
            // 范围选择首次选择的类型
            rangeFirstSide: '',
            // 上次日期变更(计算当前变更是开始还是结束时间)
            lastDateStr: [],
        };
    }

    componentDidMount() {
        this._data.lastDateStr = this.props.value ? this.props.value.split(',') : [];
    }

    componentDidUpdate(prevProps) {
        // 当 value 变化时，同步更新 lastDateStr
        if (prevProps.value !== this.props.value) {
            this._data.lastDateStr = this.props.value ? this.props.value.split(',') : [];
        }
    }

    // 设置禁用的日期
    _handleDisabled(current) {
        // 范围组件如果开启了限制天数
        if (this.props.limitDays) {
            // 如果已经完成了首次选择，或者正在选择过程中
            if (this._data.rangeFirstSide || (this.state.pickDates && this.state.pickDates.length > 0)) {
                if (this.state.pickDates && this.state.pickDates.length > 0) {
                    // 如果只选择了开始日期，禁用超过限制的结束日期
                    if (this.state.pickDates[0] && !this.state.pickDates[1]) {
                        const diffDays = current.diff(this.state.pickDates[0], 'days');
                        if (diffDays > this.props.limitDays) {
                            return true;
                        }
                    }
                    // 如果只选择了结束日期，禁用超过限制的开始日期
                    else if (this.state.pickDates[1] && !this.state.pickDates[0]) {
                        const diffDays = this.state.pickDates[1].diff(current, 'days');
                        if (diffDays > this.props.limitDays) {
                            return true;
                        }
                    }
                    // 如果两个日期都已选择，且 rangeFirstSide 存在（正在修改中），则限制范围
                    else if (this.state.pickDates[0] && this.state.pickDates[1] && this._data.rangeFirstSide) {
                        const tooLate =
                            this.state.pickDates[0] &&
                            current.diff(this.state.pickDates[0], 'days') >
                                this.props.limitDays;
                        if (tooLate) {
                            return true;
                        }
                        const tooEarly =
                            this.state.pickDates[1] &&
                            this.state.pickDates[1].diff(current, 'days') >
                                this.props.limitDays;
                        if (tooEarly) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    // 修复有天数限制时日期出界
    _fixLimitDaysOut(dates) {
        // 无限制，跳过
        if (!this.props.limitDays || !dates) {
            return;
        }
        // 变更端
        let changedSide = '';
        if (dates[0]) {
            const curStart = dates[0].format('YYYY-MM-DD');
            if (curStart !== this._data.lastDateStr[0]) {
                this._data.lastDateStr[0] = curStart;
                changedSide = 'start';
            }
        }
        if (!changedSide && dates[1]) {
            const curEnd = dates[1].format('YYYY-MM-DD');
            if (curEnd !== this._data.lastDateStr[1]) {
                this._data.lastDateStr[1] = curEnd;
                changedSide = 'end';
            }
        }
        // 设置首次选择类型
        if (!this._data.rangeFirstSide) {
            this._data.rangeFirstSide = changedSide;
        }
        // 未完成选择，跳过
        if (!dates[0] || !dates[1]) {
            return;
        }
        // 在范围内，跳过
        const difference = Math.abs(dates[0].diff(dates[1], 'days'));
        if (difference <= this.props.limitDays) {
            return;
        }
        // 开始时间的变更，置空结束
        if (changedSide === 'start') {
            dates[1] = null;
        }
        // 结束时间的变更，置空开始
        else if (changedSide === 'end') {
            dates[0] = null;
        }
    }

    // 用户操作后设置输出
    _handleChange(dates) {
        if (!dates) {
            this.props.onChange('');
            return;
        }
        this._fixLimitDaysOut(dates);
        if (dates.length !== 2 || !dates[0] || !dates[1]) {
            // 当选择未完成时，不发送改变事件
            return;
        }
        const changedValues = [
            dates[0].format('YYYY-MM-DD HH:mm:ss'),
            dates[1].format('YYYY-MM-DD HH:mm:ss'),
        ];
        this.props.onChange(changedValues.join(','));
    }

    _renderExtraFooter() {
        return () => {
            if (!this.props.limitDays || this.props.limitDays <= 0) {
                return null;
            }
            return `提示：当前最多只允许选择 ${this.props.limitDays} 天`;
        };
    }

    render() {
        const format = this.props.showSecond ? 'YYYY-M-D HH:mm:ss' : 'YYYY-M-D HH:mm';
        // 默认值转换
        const valArr = this.props.value ? this.props.value.split(',') : [null, null];
        valArr[0] = !valArr[0] || valArr[0] === '0' ? null : valArr[0];
        valArr[1] = !valArr[1] || valArr[1] === '0' ? null : valArr[1];
        const values = [
            valArr[0] && moment(valArr[0], format),
            valArr[1] && moment(valArr[1], format),
        ];
        return (
            <DatePicker.RangePicker
                className={[
                    'c-date-time-range-picker',
                    this.props.showSecond ? '' : 'no-second',
                ].join(' ')}
                style={this.props.style}
                showTime
                locale={locale}
                format={format}
                placeholder={this.props.placeholder || ['开始时间', '结束时间']}
                allowClear={this.props.allowClear}
                value={values}
                renderExtraFooter={this._renderExtraFooter()}
                disabledDate={evt => this._handleDisabled(evt)}
                onOpenChange={evt => {
                    // 重置 rangeFirstSide，允许用户重新选择
                    this._data.rangeFirstSide = '';
                    if (!evt) {
                        // 关闭日历时重置状态
                        this.setState({ pickDates: null });
                    }
                }}
                onCalendarChange={evt => this.setState({ pickDates: evt })}
                onChange={evt => this._handleChange(evt)}
            />
        );
    }
}
